import { useInfiniteQuery } from "@tanstack/react-query";
import { DefaultError } from "@tanstack/react-query";
import { useInfiniteHandler } from "hooks";
import { IInfinitePageParams, IInfinitePageResponse } from "services/types";
import { getDefaultInfinitePageParam } from "utils/service/defaultInfinitePageParam";
import { useResponseHandler } from "utils/service/responseHandler";
import { IContractProduct, IContractTrack, IContractVideo } from "./types";
import { apiClient } from "utils";

export const useGetContractProducts = (
  contractId: string,
  options?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.page,
    options?.itemCount
  );

  return useInfiniteQuery<IContractProduct[], DefaultError>({
    queryKey: ["contract-products", contractId],
    queryFn: async ({ pageParam = page }) => {
      const response = await apiClient({
        method: "GET",
        url: `Contract/Products`,
        params: {
          ContractId: contractId,
          ItemCount: itemCount,
          Page: pageParam,
        },
      });

      const result = await responseHandler<
        IInfinitePageResponse<IContractProduct>
      >(response, {
        groupName: `useGetContractProducts - ${pageParam}`,
      });

      return infiniteHandler<IContractProduct>(result);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled: !!contractId,
  });
};

export const useGetContractTracks = (
  contractId: string,
  options?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.page,
    options?.itemCount
  );

  return useInfiniteQuery<IContractTrack[], DefaultError>({
    queryKey: ["contract-tracks", contractId],
    queryFn: async ({ pageParam = page }) => {
      const response = await apiClient({
        method: "GET",
        url: `Contract/Tracks`,
        params: {
          ContractId: contractId,
          ItemCount: itemCount,
          Page: pageParam,
        },
      });

      const result = await responseHandler<
        IInfinitePageResponse<IContractTrack>
      >(response, {
        groupName: `useGetContractTracks - ${pageParam}`,
      });

      return infiniteHandler<IContractTrack>(result);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled: !!contractId,
  });
};

export const useGetContractVideos = (
  contractId: string,
  options?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.page,
    options?.itemCount
  );

  return useInfiniteQuery<IContractVideo[], DefaultError>({
    queryKey: ["contract-videos", contractId],
    queryFn: async ({ pageParam = page }) => {
      const response = await apiClient({
        method: "GET",
        url: `Contract/Videos`, // 🔹 dopasuj do endpointu backendu
        params: {
          ContractId: contractId,
          ItemCount: itemCount,
          Page: pageParam,
        },
      });

      const result = await responseHandler<
        IInfinitePageResponse<IContractVideo>
      >(response, {
        groupName: `useGetContractVideos - ${pageParam}`,
      });

      return infiniteHandler<IContractVideo>(result);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled: !!contractId,
  });
};
